import React from "react";
import { Typography } from "@mui/material";
import { useLocale } from "../../i18n/I18nProvider";
import { useT } from "../../i18n/useT";
import "./languageSwitcher.scss";

// Language dropdown. Enabled UI languages are English, Japanese and Chinese.
// Reads/writes the reactive locale store, so a selection re-renders the whole
// app and updates `<html lang>` + persistence. The initial value comes from the
// store, which resolves ?locale= -> stored choice -> device/browser language
// (including the language the native shell reports through the WebView).
const OPTIONS = [
  { code: "en", key: "english" },
  { code: "ja", key: "japanese" },
  { code: "zh", key: "chinese" },
];

const LanguageSwitcher = () => {
  const { locale, setLocale } = useLocale();
  const t = useT("language");

  const selectLocale = (code) => {
    setLocale(code);

    // Keep the URL aligned with the live choice. Authentication or a WebView
    // reload must not revive an older ?locale= value after another language was
    // selected on the login screen.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("locale", code);
      window.history.replaceState(
        window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
    } catch {
      // Locale state and storage still work if URL mutation is unavailable.
    }
  };

  // Reflect the active locale; if it is one this control does not list, show
  // English selected rather than a blank dropdown.
  const value = OPTIONS.some((option) => option.code === locale)
    ? locale
    : "en";

  return (
    <div className="language-switcher">
      <Typography
        className="language-switcher-label"
        component="label"
        htmlFor="language-select"
      >
        {t("language")}
      </Typography>
      <select
        id="language-select"
        className="language-switcher-select"
        value={value}
        aria-label={t("language")}
        onChange={(event) => selectLocale(event.target.value)}
      >
        {OPTIONS.map(({ code, key }) => (
          <option key={code} value={code} lang={code}>
            {t(key)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LanguageSwitcher;
